import clsx from 'clsx';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown, GripHorizontal, Loader2, Search, X } from 'lucide-react';
import {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  Fragment,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type FocusEvent as ReactFocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { matchesSearch, searchText, type EntryStatus } from '@pz/core';
import { RpcError } from '../lib/rpc';

/* ------------------------------------------------------------------ */
/* Buttons                                                              */
/* ------------------------------------------------------------------ */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-accent-ink border-accent hover:brightness-110',
  secondary: 'bg-sheet text-ink border-line-strong hover:bg-sheet-2',
  ghost: 'bg-transparent text-ink border-transparent hover:bg-sheet-2',
  danger: 'bg-sheet text-bad border-line-strong hover:bg-bad-soft',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm' | 'md';
  icon?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, loading, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-md border font-medium whitespace-nowrap transition-[filter,background-color] disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'h-7 px-2 text-[13px]' : 'h-9 px-3 text-sm',
        variants[variant],
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
});

export function IconButton({ label, icon, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; icon: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={clsx('inline-flex size-8 items-center justify-center rounded-md text-graphite hover:bg-sheet-2 hover:text-ink disabled:opacity-40', className)}
      {...rest}
    >
      {icon}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Form controls                                                        */
/* ------------------------------------------------------------------ */

const hasWidth = (c?: string) => !!c && /(^|\s)(w-|min-w-|max-w-|flex-1)/.test(c);
/** A height or padding given by the caller replaces the default (two utilities of one property: the CSS order decides). */
const hasHeight = (c?: string) => !!c && /(^|\s)h-/.test(c);

const control =
  'rounded-md border border-line-strong bg-sheet px-2.5 text-sm text-ink placeholder:text-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25 disabled:bg-sheet-2 disabled:text-graphite';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={clsx(control, !hasHeight(className) && 'h-9', !hasWidth(className) && 'w-full', className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...rest }, ref) {
  return <textarea ref={ref} className={clsx(control, 'min-h-20 py-2 leading-snug', !hasWidth(className) && 'w-full', className)} {...rest} />;
});

/* ------------------------------------------------------------------ */
/* Select: a list with search                                           */
/* ------------------------------------------------------------------ */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

/** What onChange receives: the same shape as a change event of a native select (e.target.value). */
export interface SelectChangeEvent {
  target: { value: string; name?: string };
  currentTarget: { value: string; name?: string };
}

export interface SelectProps {
  value?: string | number | null;
  onChange?: (e: SelectChangeEvent) => void;
  /** <option> and <optgroup> elements, like a native select */
  children?: ReactNode;
  /** or the options as data */
  options?: SelectOption[];
  className?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  title?: string;
  placeholder?: string;
  /** always a search field (lists that grow, e.g. projects), not only from 8 entries */
  search?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

/** Search in lists: one rule for the server and the pages (packages/core/src/search.ts). */
export { searchText, matchesSearch };

function nodeText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (isValidElement(node)) return nodeText((node.props as { children?: ReactNode }).children);
  return '';
}

function optionsFromChildren(children: ReactNode): SelectOption[] {
  const out: SelectOption[] = [];
  const walk = (nodes: ReactNode, group?: string) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return;
      const props = child.props as { children?: ReactNode; value?: string | number; label?: string; disabled?: boolean };
      if (child.type === Fragment) walk(props.children, group);
      else if (child.type === 'optgroup') walk(props.children, props.label);
      else if (child.type === 'option') {
        const label = (props.label ?? nodeText(props.children)).replace(/\s+/g, ' ').trim();
        out.push({ value: String(props.value ?? label), label, disabled: props.disabled, group });
      }
    });
  };
  walk(children);
  return out;
}

/** Lists with more entries get a search field; shorter ones jump to the typed letters. */
const SEARCH_FROM = 8;
const MAX_SHOWN = 400;

/**
 * Drop-down list with search (replaces the native select everywhere): type to filter, arrow keys, Enter,
 * Escape. Long lists (projects, people, tasks) need no scrolling: a few letters or numbers find the entry.
 * Accepts <option>/<optgroup> children and calls onChange with e.target.value like a native select.
 */
export function Select({ value, onChange, children, options: given, className, disabled, id, name, title, placeholder, search, ...aria }: SelectProps) {
  const { t } = useTranslation();
  const options = useMemo(() => given ?? optionsFromChildren(children), [given, children]);
  const current = value === null || value === undefined ? '' : String(value);
  const selected = options.find((o) => o.value === current);
  const searchable = search ?? options.length >= SEARCH_FROM;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number; width: number; maxHeight: number; up: boolean } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const listId = useId();

  const filtered = useMemo(() => (query.trim() ? options.filter((o) => matchesSearch(`${o.label} ${o.group ?? ''}`, query)) : options), [options, query]);
  const shown = filtered.length > MAX_SHOWN ? filtered.slice(0, MAX_SHOWN) : filtered;

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const below = vh - r.bottom - 8;
    const above = r.top - 8;
    const up = below < 240 && above > below;
    const width = Math.min(Math.max(r.width, 240), vw - 16);
    setPos({
      left: Math.min(Math.max(8, r.left), vw - width - 8),
      top: up ? r.top - 4 : r.bottom + 4,
      width,
      maxHeight: Math.max(160, Math.min(380, up ? above : below)),
      up,
    });
  }, []);

  const openList = (initial = '') => {
    if (disabled) return;
    setQuery(initial);
    const idx = options.findIndex((o) => o.value === current);
    setActive(initial ? 0 : Math.max(0, idx));
    place();
    setOpen(true);
  };
  const close = (focusTrigger = true) => {
    setOpen(false);
    if (focusTrigger) triggerRef.current?.focus();
  };
  const choose = (o: SelectOption | undefined) => {
    if (!o || o.disabled) return;
    close();
    if (o.value !== current) onChange?.({ target: { value: o.value, name }, currentTarget: { value: o.value, name } });
  };

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => (searchRef.current ?? listRef.current)?.focus());
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (popRef.current?.contains(e.target as Node)) return;
      place();
    };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open, shown.length]);

  const step = (from: number, dir: number) => {
    const n = shown.length;
    if (!n) return 0;
    let i = from;
    for (let k = 0; k < n; k++) {
      i = Math.min(n - 1, Math.max(0, i + dir));
      if (!shown[i]?.disabled) return i;
      if (i === 0 || i === n - 1) break;
    }
    return from;
  };
  /** Jumps to the next entry starting with the typed letters (lists without search field, and the closed list). */
  const typeahead = (key: string): SelectOption | undefined => {
    const now = Date.now();
    typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + key, at: now };
    const q = searchText(typed.current.text);
    return options.find((o) => !o.disabled && searchText(o.label).startsWith(q));
  };

  const onListKey = (e: ReactKeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((a) => step(a, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((a) => step(a, -1));
        break;
      case 'PageDown':
        e.preventDefault();
        setActive((a) => step(Math.min(shown.length - 1, a + 9), 1));
        break;
      case 'PageUp':
        e.preventDefault();
        setActive((a) => step(Math.max(0, a - 9), -1));
        break;
      case 'Home':
      case 'End':
        if (searchable && e.target === searchRef.current) break;
        e.preventDefault();
        setActive(e.key === 'Home' ? step(-1, 1) : step(shown.length, -1));
        break;
      case 'Enter':
        e.preventDefault();
        choose(shown[active]);
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        close();
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (!searchable && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          const hit = typeahead(e.key);
          if (hit) setActive(shown.indexOf(hit));
        }
    }
  };

  const onTriggerKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (['ArrowDown', 'ArrowUp', 'Enter', ' ', 'F4'].includes(e.key)) {
      e.preventDefault();
      openList();
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      if (searchable) openList(e.key);
      else choose(typeahead(e.key));
    }
  };

  const activeId = shown[active] ? `${listId}-o${active}` : undefined;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={aria['aria-label']}
        aria-labelledby={aria['aria-labelledby']}
        title={title ?? selected?.label}
        disabled={disabled}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={onTriggerKey}
        className={clsx(
          control,
          'inline-flex items-center gap-1.5 pr-2 text-left',
          !/(^|\s)h-\d/.test(className ?? '') && 'h-9',
          !hasWidth(className) && 'w-full',
          open && 'border-accent ring-2 ring-accent/25',
          className,
        )}
      >
        <span className={clsx('min-w-0 flex-1 truncate', !selected && 'text-faint')}>{selected?.label ?? placeholder ?? ''}</span>
        <ChevronDown className={clsx('size-4 shrink-0 text-graphite transition-transform', open && 'rotate-180')} aria-hidden />
      </button>
      {name && <input type="hidden" name={name} value={current} />}
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            className="fixed z-[70] flex flex-col overflow-hidden rounded-md border border-line-strong bg-sheet shadow-xl print:hidden"
            style={{ left: pos.left, top: pos.top, width: pos.width, maxHeight: pos.maxHeight, transform: pos.up ? 'translateY(-100%)' : undefined }}
          >
            {searchable && (
              <div className="flex items-center gap-2 border-b border-line px-2.5">
                <Search className="size-4 shrink-0 text-graphite" aria-hidden />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActive(0);
                  }}
                  onKeyDown={onListKey}
                  placeholder={t('common.typeToSearch')}
                  aria-label={t('common.typeToSearch')}
                  aria-controls={listId}
                  aria-activedescendant={activeId}
                  aria-autocomplete="list"
                  className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-faint"
                />
                {query && (
                  <span className="num shrink-0 text-xs text-graphite">
                    {filtered.length}/{options.length}
                  </span>
                )}
              </div>
            )}
            <div
              ref={listRef}
              id={listId}
              role="listbox"
              tabIndex={-1}
              aria-label={aria['aria-label']}
              aria-activedescendant={searchable ? undefined : activeId}
              onKeyDown={searchable ? undefined : onListKey}
              className="min-h-0 overflow-y-auto py-1 outline-none"
            >
              {shown.map((o, i) => (
                <Fragment key={`${o.group ?? ''}\u0000${o.value}\u0000${i}`}>
                  {o.group && o.group !== shown[i - 1]?.group && (
                    <div role="presentation" className="px-3 pt-2 pb-0.5 text-[11px] font-semibold tracking-wide text-faint uppercase">
                      {o.group}
                    </div>
                  )}
                  <div
                    id={`${listId}-o${i}`}
                    role="option"
                    data-index={i}
                    aria-selected={o.value === current}
                    aria-disabled={o.disabled || undefined}
                    title={o.label.length > 40 ? o.label : undefined}
                    onPointerMove={() => i !== active && setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(o)}
                    className={clsx(
                      'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm',
                      i === active && !o.disabled && 'bg-accent-soft',
                      o.disabled && 'cursor-not-allowed text-faint',
                      o.value === current && 'font-medium',
                    )}
                  >
                    <span className="min-w-0 flex-1 truncate">{o.label || ' '}</span>
                    {o.value === current && <Check className="size-4 shrink-0 text-accent" aria-hidden />}
                  </div>
                </Fragment>
              ))}
              {!shown.length && <div className="px-3 py-2 text-sm text-graphite">{t('common.noMatches')}</div>}
              {filtered.length > shown.length && <div className="px-3 py-1.5 text-xs text-graphite">{t('common.moreMatches', { count: filtered.length - shown.length })}</div>}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export function Checkbox({ label, hint, className, indeterminate, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; hint?: ReactNode; indeterminate?: boolean }) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <div className={clsx('flex items-start gap-2', className)}>
      <input ref={ref} id={rest.id ?? id} type="checkbox" className="mt-0.5 size-4 accent-[var(--accent)]" {...rest} />
      <label htmlFor={rest.id ?? id} className="text-sm leading-snug">
        {label}
        {hint && <span className="block text-xs text-graphite">{hint}</span>}
      </label>
    </div>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
  htmlFor,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  htmlFor?: string;
}) {
  // the label names the control: a single control without id gets a generated one (screen readers, label click)
  const generated = useId();
  const single = !htmlFor && Children.count(children) === 1 && isValidElement(children) ? (children as ReactElement<{ id?: string }>) : null;
  const forId = htmlFor ?? single?.props.id ?? (single ? generated : undefined);
  const control = single && !single.props.id ? cloneElement(single, { id: generated }) : children;
  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      <label htmlFor={forId} className="text-[13px] font-medium text-graphite">
        {label}
      </label>
      {control}
      {error ? <span className="text-xs text-bad">{error}</span> : hint ? <span className="text-xs text-graphite">{hint}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Surfaces and labels                                                  */
/* ------------------------------------------------------------------ */

export function Panel({ title, actions, children, className, bodyClassName }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; bodyClassName?: string }) {
  return (
    <section className={clsx('rounded-lg border border-line bg-sheet', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
          {title && <h2 className="text-[15px] font-semibold">{title}</h2>}
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </header>
      )}
      {/* bodyClassName replaces the default padding (e.g. p-0 for tables flush with the panel edges) */}
      <div className={bodyClassName ?? 'p-4'}>{children}</div>
    </section>
  );
}

export type Tone = 'neutral' | 'accent' | 'good' | 'warn' | 'bad' | 'info';

const tones: Record<Tone, string> = {
  neutral: 'bg-sheet-2 text-graphite border-line',
  accent: 'bg-accent-soft text-accent border-transparent',
  good: 'bg-good-soft text-good border-transparent',
  warn: 'bg-warn-soft text-warn border-transparent',
  bad: 'bg-bad-soft text-bad border-transparent',
  info: 'bg-info-soft text-info border-transparent',
};

export function Pill({ tone = 'neutral', children, className, title }: { tone?: Tone; children: ReactNode; className?: string; title?: string }) {
  return (
    <span title={title} className={clsx('inline-flex items-center gap-1 rounded-full border px-2 py-px text-xs font-medium whitespace-nowrap', tones[tone], className)}>
      {children}
    </span>
  );
}

export const STATUS_TONE: Record<EntryStatus, Tone> = {
  draft: 'neutral',
  submitted: 'info',
  approved_l1: 'accent',
  approved: 'good',
  rejected: 'bad',
  billed: 'good',
};

export function StatusPill({ status }: { status: EntryStatus }) {
  const { t } = useTranslation();
  return <Pill tone={STATUS_TONE[status]}>{t(`status.${status}`)}</Pill>;
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-[-0.01em]">{title}</h1>
        {subtitle && <p className="mt-0.5 max-w-[70ch] text-sm text-graphite">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Tabs<T extends string>({ tabs, value, onChange }: { tabs: { id: T; label: ReactNode; icon?: ReactNode; hidden?: boolean }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="scroll-x mb-4 flex gap-1 border-b border-line" role="tablist">
      {tabs
        .filter((t) => !t.hidden)
        .map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={value === tab.id}
            onClick={() => onChange(tab.id)}
            className={clsx(
              '-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap',
              value === tab.id ? 'border-accent text-ink' : 'border-transparent text-graphite hover:text-ink',
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
    </div>
  );
}

export function EmptyState({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-line-strong px-4 py-8 text-center text-sm text-graphite">
      <div className="max-w-[60ch]">{children}</div>
      {action}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('size-5 animate-spin text-graphite', className)} aria-hidden />;
}

export function Loading() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 py-8 text-sm text-graphite">
      <Spinner /> {t('app.loading')}
    </div>
  );
}

export function useErrorText() {
  const { t } = useTranslation();
  return useCallback(
    (e: unknown): string => {
      if (e instanceof RpcError) {
        if (e.code === 'NOT_BOOKABLE' && e.details && typeof e.details === 'object' && 'reason' in e.details) {
          const reason = String((e.details as { reason: string }).reason);
          return t(`errors.bookability.${reason}` as 'errors.bookability.NOT_MEMBER', { defaultValue: t('errors.NOT_BOOKABLE') });
        }
        if ((e.code === 'CONFLICT' || e.code === 'VALIDATION') && e.details && typeof e.details === 'object' && (e.details as { field?: string }).field === 'shortCode') {
          return t(e.code === 'CONFLICT' ? 'errors.shortCodeTaken' : 'errors.shortCodeInvalid');
        }
        if (e.code === 'VALIDATION' && e.details && typeof e.details === 'object' && (e.details as { field?: string }).field === 'taskId') return t('quick.favoritesFull');
        if (e.code === 'VALIDATION' && Array.isArray(e.details) && e.details.length) {
          const first = e.details[0] as { path?: string; message?: string };
          return `${t('errors.VALIDATION')} ${first.path ? `(${first.path})` : ''}`.trim();
        }
        // a reason of its own (edit file, preview): a sentence that says what to do
        const reason = e.details && typeof e.details === 'object' && !Array.isArray(e.details) ? (e.details as { reason?: unknown }).reason : undefined;
        if (typeof reason === 'string' && /^[A-Z_]{3,40}$/.test(reason)) {
          const text = t(`errors.reasons.${reason}` as 'errors.reasons.NOT_EDIT_FILE', { defaultValue: '' });
          if (text) return text;
        }
        // a field of a form that the server refused (e.g. a date that does not exist)
        const field = e.details && typeof e.details === 'object' && !Array.isArray(e.details) ? (e.details as { field?: unknown }).field : undefined;
        if (e.code === 'VALIDATION' && typeof field === 'string' && /^[a-zA-Z]{2,40}$/.test(field)) {
          const label = t(`billing.fields.${field}` as 'billing.fields.title', { defaultValue: '' });
          if (label) return `${t('errors.VALIDATION')} (${label})`;
        }
        return t(`errors.${e.code}` as 'errors.INTERNAL', { defaultValue: e.message || t('errors.INTERNAL') });
      }
      return t('errors.INTERNAL');
    },
    [t],
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const text = useErrorText();
  if (!error) return null;
  return <div className="rounded-md border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">{text(error)}</div>;
}

export function Notice({ tone = 'info', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <div className={clsx('rounded-md border px-3 py-2 text-sm', tones[tone], tone !== 'neutral' && 'border-current/20', className)}>{children}</div>;
}

/* ------------------------------------------------------------------ */
/* Modal                                                                */
/* ------------------------------------------------------------------ */

/** Dialog positions moved by the person, per dialog (this browser only; a convenience, nothing depends on it). */
const MODAL_POS_KEY = 'pz.modal.pos';
function readModalPos(id: string): { x: number; y: number } | null {
  try {
    const all = JSON.parse(localStorage.getItem(MODAL_POS_KEY) ?? '{}') as Record<string, { x: number; y: number }>;
    const p = all[id];
    return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null;
  } catch {
    return null;
  }
}
function writeModalPos(id: string, pos: { x: number; y: number } | null) {
  try {
    const all = JSON.parse(localStorage.getItem(MODAL_POS_KEY) ?? '{}') as Record<string, { x: number; y: number }>;
    if (pos) all[id] = pos;
    else delete all[id];
    localStorage.setItem(MODAL_POS_KEY, JSON.stringify(all));
  } catch {
    /* private mode: the position is kept for this visit only */
  }
}

/**
 * Dialog. On larger screens it can be moved by its title bar (drag; double click: back to the start position), so the
 * page behind stays readable; `id` remembers where the person put it. `side`: on wide screens the dialog opens at the
 * right edge over a light backdrop, e.g. the booking dialog over "Meine Woche", where the days stay visible on the left.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
  side,
  id,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  side?: boolean;
  id?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const drag = useRef<{ sx: number; sy: number; ox: number; oy: number; rect: DOMRect } | null>(null);
  useEffect(() => {
    if (open) setPos((id && readModalPos(id)) || { x: 0, y: 0 });
  }, [open, id]);
  // a remembered position that no longer fits the window (smaller window, other screen) falls back to the start
  useEffect(() => {
    if (!open || (!pos.x && !pos.y) || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    if (r.right < 120 || r.left > window.innerWidth - 120 || r.top < 0 || r.top > window.innerHeight - 48) setPos({ x: 0, y: 0 });
  }, [open, pos]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    // keep the focus of a field with autoFocus inside the dialog; otherwise focus the first field
    if (!ref.current?.contains(document.activeElement)) {
      const first = ref.current?.querySelector<HTMLElement>('input:not([type=hidden]):not([disabled]):not([role=combobox]), textarea, select');
      first?.focus();
    }
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    // small screens: the dialog is a sheet at the bottom and does not move; buttons in the title bar stay buttons
    if (e.button !== 0 || window.innerWidth < 640 || (e.target as HTMLElement).closest('button, a, input, select, textarea') || !ref.current) return;
    drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, rect: ref.current.getBoundingClientRect() };
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    // at least the title bar stays reachable: 120 px of the width and the bar itself inside the window
    const dx = Math.max(120 - d.rect.right, Math.min(window.innerWidth - 120 - d.rect.left, e.clientX - d.sx));
    const dy = Math.max(8 - d.rect.top, Math.min(window.innerHeight - 56 - d.rect.top, e.clientY - d.sy));
    setPos({ x: d.ox + dx, y: d.oy + dy });
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    if (id) writeModalPos(id, pos.x || pos.y ? pos : null);
  };
  const moved = pos.x !== 0 || pos.y !== 0;
  return (
    <div
      className={clsx(
        'fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6 print:hidden',
        side ? 'bg-ink/30 sm:bg-ink/15 xl:justify-end xl:bg-ink/5 xl:pr-8' : 'bg-ink/30',
      )}
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        style={moved ? { transform: `translate(${pos.x}px, ${pos.y}px)` } : undefined}
        className={clsx(
          'flex max-h-[92vh] w-full flex-col rounded-t-xl border border-line bg-sheet shadow-xl sm:rounded-xl',
          wide ? 'sm:max-w-4xl' : 'sm:max-w-lg',
          side && 'xl:shadow-2xl',
        )}
      >
        <header
          className="flex items-center justify-between gap-2 border-b border-line px-4 py-3 select-none sm:cursor-move"
          title={t('common.moveDialog')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={(e) => {
            if ((e.target as HTMLElement).closest('button')) return;
            setPos({ x: 0, y: 0 });
            if (id) writeModalPos(id, null);
          }}
        >
          <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold">
            <GripHorizontal className="hidden size-4 shrink-0 text-faint sm:block" aria-hidden />
            <span className="min-w-0">{title}</span>
          </h2>
          <IconButton label={t('common.close')} icon={<X className="size-4" />} onClick={onClose} />
        </header>
        <div className="overflow-y-auto px-4 py-4">{children}</div>
        {footer && <footer className="flex flex-wrap justify-end gap-2 border-t border-line px-4 py-3">{footer}</footer>}
      </div>
    </div>
  );
}

/** Confirmation inside the page (the demo viewer blocks window.confirm). Optional reason field. */
export function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  text,
  confirmLabel,
  danger,
  reason,
  reasonLabel,
  loading,
  error,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  title: ReactNode;
  text?: ReactNode;
  confirmLabel: ReactNode;
  danger?: boolean;
  reason?: 'required' | 'optional';
  reasonLabel?: ReactNode;
  loading?: boolean;
  error?: unknown;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  useEffect(() => {
    if (open) setValue('');
  }, [open]);
  const disabled = reason === 'required' && value.trim().length < 3;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant={danger ? 'danger' : 'primary'} disabled={disabled} loading={loading} onClick={() => onConfirm(value.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {text && <p className="text-sm text-graphite">{text}</p>}
        {reason && (
          <Field label={reasonLabel ?? t('common.reason')} hint={t('common.reasonHint')}>
            <Textarea value={value} onChange={(e) => setValue(e.target.value)} rows={3} />
          </Field>
        )}
        <ErrorBox error={error} />
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/* Toasts                                                               */
/* ------------------------------------------------------------------ */

interface ToastAction {
  label: string;
  run: () => Promise<unknown> | void;
}

interface ToastItem {
  id: number;
  text: string;
  tone: Tone;
  action?: ToastAction;
}

const ToastContext = createContext<{ show: (text: string, tone?: Tone, action?: ToastAction) => void }>({ show: () => {} });

/**
 * Toasts. A toast with an action ("Rückgängig") stays 10 seconds; the action runs once. What was
 * undone stays visible in the history of the entry.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const remove = useCallback((id: number) => setItems((x) => x.filter((i) => i.id !== id)), []);
  const show = useCallback(
    (text: string, tone: Tone = 'good', action?: ToastAction) => {
      const id = Date.now() + Math.random();
      setItems((x) => [...x, { id, text, tone, action }]);
      setTimeout(() => remove(id), action ? 10_000 : tone === 'bad' ? 7000 : 3500);
    },
    [remove],
  );
  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div className="pointer-events-none fixed right-4 bottom-4 z-[60] flex max-w-sm flex-col gap-2 print:hidden" aria-live="polite">
        {items.map((i) => (
          <div key={i.id} className={clsx('pointer-events-auto flex items-center gap-3 rounded-md border px-3 py-2 text-sm shadow-lg', tones[i.tone], 'border-current/20 bg-sheet')}>
            <span className="flex-1">{i.text}</span>
            {i.action && (
              <button
                type="button"
                className="shrink-0 rounded px-1.5 py-0.5 font-semibold text-accent underline-offset-2 hover:underline"
                onClick={() => {
                  const a = i.action!;
                  remove(i.id);
                  void Promise.resolve(a.run()).catch(() => {});
                }}
              >
                {i.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  const errText = useErrorText();
  return {
    ok: (text: string) => ctx.show(text, 'good'),
    info: (text: string) => ctx.show(text, 'info'),
    warn: (text: string) => ctx.show(text, 'warn'),
    error: (e: unknown) => ctx.show(typeof e === 'string' ? e : errText(e), 'bad'),
    /** Success message with an undo button (10 seconds). */
    undoable: (text: string, label: string, run: () => Promise<unknown> | void) => ctx.show(text, 'good', { label, run }),
  };
}

/* ------------------------------------------------------------------ */
/* Figures                                                              */
/* ------------------------------------------------------------------ */

/**
 * Horizontal gauge of the hours booked against the target. `base` = contractual minutes (the part above the target is hatched: holiday or absence);
 * `comp` = time off in lieu inside the target (hatched right after the booked part, so booked + comp fill the target).
 */
export function Gauge({ value, capacity, base, comp = 0, className }: { value: number; capacity: number; base?: number; comp?: number; className?: string }) {
  const max = Math.max(value, base ?? capacity, 1);
  const capPct = (capacity / max) * 100;
  const unavailablePct = base && base > capacity ? ((base - capacity) / max) * 100 : 0;
  const valPct = Math.min(100, (value / max) * 100);
  const compPct = comp > 0 ? Math.max(0, Math.min(capPct - valPct, (comp / max) * 100)) : 0;
  // above the target, or anything booked on a day without target (weekend, holiday): extra hours
  const over = value > capacity && (capacity > 0 || value > 0);
  // exactly the target: the day (or week) is complete
  const full = capacity > 0 && value === capacity;
  return (
    <div className={clsx('relative h-1.5 w-full overflow-hidden rounded-full bg-accent-soft', className)} aria-hidden>
      {unavailablePct > 0 && <div className="hatch absolute inset-y-0" style={{ left: `${capPct}%`, width: `${unavailablePct}%` }} />}
      {compPct > 0 && <div className="hatch absolute inset-y-0" style={{ left: `${valPct}%`, width: `${compPct}%` }} />}
      <div className={clsx('absolute inset-y-0 left-0 rounded-full', over ? 'bg-warn' : full ? 'bg-good' : 'bg-accent')} style={{ width: `${valPct}%` }} />
      {capacity > 0 && <div className="absolute inset-y-0 w-px bg-ink/50" style={{ left: `calc(${capPct}% - 1px)` }} />}
    </div>
  );
}

export function Stat({ label, value, hint, tone }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: Tone }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[13px] text-graphite">{label}</span>
      <span className={clsx('text-xl font-semibold', tone === 'bad' && 'text-bad', tone === 'warn' && 'text-warn', tone === 'good' && 'text-good')}>{value}</span>
      {hint && <span className="text-xs text-graphite">{hint}</span>}
    </div>
  );
}

export const th = 'px-3 py-2 text-left text-[12px] font-medium text-graphite whitespace-nowrap';

/**
 * Column head that sorts the list (lib/sort: the choice is stored per person). The arrow shows the direction; the
 * state is announced to screen readers (aria-sort).
 */
export function SortTh({
  label,
  sortKey,
  sort,
  onSort,
  className,
  right,
}: {
  label: ReactNode;
  sortKey: string;
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
  className?: string;
  right?: boolean;
}) {
  const active = sort?.key === sortKey;
  return (
    <th className={clsx(th, right && 'text-right', className)} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <SortButton label={label} sortKey={sortKey} sort={sort} onSort={onSort} right={right} />
    </th>
  );
}

/** The button of a sorting column head; several of them can share one head (e.g. number and title of a task). */
export function SortButton({
  label,
  sortKey,
  sort,
  onSort,
  right,
}: {
  label: ReactNode;
  sortKey: string;
  sort: { key: string; dir: 'asc' | 'desc' } | null;
  onSort: (key: string) => void;
  right?: boolean;
}) {
  const { t } = useTranslation();
  const active = sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      title={active ? (sort.dir === 'asc' ? t('common.sortDesc') : t('common.sortReset')) : t('common.sortAsc')}
      className={clsx('inline-flex items-center gap-1 rounded hover:text-ink', active && 'text-ink', right && 'flex-row-reverse')}
    >
      {label}
      <Icon className={clsx('size-3 shrink-0', !active && 'opacity-40')} aria-hidden />
    </button>
  );
}
export const td = 'px-3 py-2 align-top';

/* ------------------------------------------------------------------ */
/* Tooltip for dense grids (hover and keyboard focus, never clipped)   */
/* ------------------------------------------------------------------ */

interface TipState {
  content: ReactNode;
  x: number;
  y: number;
  below: boolean;
}

/** Plain text of a tooltip: one line per block (div) of its content. */
function tipText(content: ReactNode): string {
  if (isValidElement(content)) {
    const kids = Children.toArray((content.props as { children?: ReactNode }).children);
    if (kids.length > 1 && kids.every((k) => isValidElement(k) || typeof k === 'string')) {
      return kids
        .map((k) => nodeText(k).replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .join('\n');
    }
  }
  return nodeText(content).replace(/\s+/g, ' ').trim();
}

export function useTip() {
  const [tip, setTip] = useState<TipState | null>(null);
  useEffect(() => {
    if (!tip) return;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [tip]);
  const bind = (content: ReactNode) => {
    const show = (el: Element) => {
      const r = el.getBoundingClientRect();
      const below = r.top < 140;
      const x = Math.min(Math.max(r.left + r.width / 2, 150), window.innerWidth - 150);
      setTip({ content, x, y: below ? r.bottom + 6 : r.top - 6, below });
    };
    return {
      onPointerEnter: (e: ReactPointerEvent<Element>) => show(e.currentTarget),
      onPointerLeave: () => setTip(null),
      onFocus: (e: ReactFocusEvent<Element>) => show(e.currentTarget),
      onBlur: () => setTip(null),
      // the same text as a plain attribute: the interactive HTML export shows it on hover without the application
      'data-tip': tipText(content),
    };
  };
  const node = tip
    ? createPortal(
        <div
          role="tooltip"
          className="pointer-events-none fixed z-50 w-max max-w-96 rounded-md border border-line bg-sheet px-2.5 py-1.5 text-xs text-ink shadow-md"
          style={{ left: tip.x, top: tip.y, transform: `translate(-50%, ${tip.below ? '0' : '-100%'})` }}
        >
          {tip.content}
        </div>,
        document.body,
      )
    : null;
  return { bind, node };
}
